import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { api, ApiError } from '../lib/api';
import { useAsync, useSession, useToast } from '../lib/store';
import type {
  Grove,
  GroveActivityEntry,
  GroveBackgroundOption,
  GroveDecorStock,
  GroveDecoration,
  GroveFeedResult,
  GroveView,
  Pokemon,
} from '../lib/types';
import { Bar, Button, EmptyState, ErrorState, Modal, Panel, Tabs } from '../components/ui';
import { GroveScene, type DragState } from '../components/GroveScene';
import { PhotoFrame, PhotoModeBar, usePhotoMode } from '../components/PhotoMode';
import { formatNumber, timeAgo } from '../lib/format';
import { feedToast } from '../lib/reciprocal';

/**
 * The Grove screen.
 *
 * A player keeps many Groves, so this page is really two things stacked: a
 * *manager* that says which habitat you are looking at, and a *scene* that
 * draws it. Switching between them is instant and never leaves the page —
 * organising a collection means moving between habitats constantly, and a
 * navigation per switch would make sorting thirty Pokemon unbearable.
 */
export function GrovePage() {
  const { username } = useParams();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const { me, setWallet, celebrate, celebrateBadges, refresh } = useSession();
  const { push } = useToast();

  const groveId = params.get('g');
  const base = username ? `/grove/${username}` : '/grove';
  const path = groveId ? `${base}?groveId=${encodeURIComponent(groveId)}` : base;

  const state = useAsync<GroveView>(() => api.get<GroveView>(path), [path]);
  const view = state.data;

  // Photo Mode. Its whole state lives in one hook so the page only has to know
  // when to turn it on — the tint, the pan, the capture and the Escape handler
  // are none of this file's business.
  const photo = usePhotoMode();

  const [editing, setEditing] = useState(false);
  // `pinned` records how the panel was opened. A click pins it (scrim, closes
  // on click-away); a hover leaves it unpinned so it follows the mouse and
  // feeding costs a single click rather than two.
  const [selected, setSelected] = useState<
    { pokemon: Pokemon; x: number; y: number; pinned: boolean } | null
  >(null);
  const [panelPos, setPanelPos] = useState<{ left: number; top: number }>({ left: 0, top: 0 });
  /** Grace period so the pointer can travel from sprite to panel without it vanishing. */
  const closeTimer = useRef<number | null>(null);
  const [tray, setTray] = useState<'pokemon' | 'decor' | 'look' | 'manage' | null>(null);
  const [busy, setBusy] = useState(false);
  const [floats, setFloats] = useState<Array<{ id: number; x: number; y: number; text: string; big: boolean }>>([]);
  const [fedIds, setFedIds] = useState<Set<string>>(new Set());
  const floatId = useRef(1);

  /** Layout edits are local until Save, so Cancel is a genuine undo. */
  const [draft, setDraft] = useState<GroveView | null>(null);
  const working = draft ?? view;

  // Seed the cooldown set once per trainer. Keying this on `view` instead would
  // re-run after every feed and wipe the mark we just added locally, since the
  // payload it reads from was fetched before that feed happened.
  const viewedOwnerId = view?.owner.userId;
  useEffect(() => {
    if (view) setFedIds(new Set(view.viewer.fedPokemonIds));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewedOwnerId]);

  // Switching Groves closes anything anchored to a sprite in the old one.
  useEffect(() => {
    setSelected(null);
    setDraft(null);
    setEditing(false);
  }, [groveId]);

  const activity = useAsync<{ activity: GroveActivityEntry[] }>(
    () => api.get(`/grove/${view?.owner.username ?? me?.user.username}/activity`),
    [view?.owner.username],
  );

  const cancelClose = useCallback(() => {
    if (closeTimer.current !== null) {
      window.clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  }, []);

  /**
   * Closes a hover-opened panel shortly after the pointer leaves it. The delay
   * is what makes the gap between the sprite and the panel crossable; without
   * it the panel disappears the instant you move toward the Feed button.
   * A pinned panel ignores this entirely.
   */
  const scheduleClose = useCallback(() => {
    cancelClose();
    closeTimer.current = window.setTimeout(() => {
      setSelected((current) => (current && !current.pinned ? null : current));
    }, 220);
  }, [cancelClose]);

  useEffect(() => cancelClose, [cancelClose]);

  const showFloat = useCallback((x: number, y: number, text: string, big = false) => {
    const id = floatId.current++;
    setFloats((current) => [...current, { id, x, y, text, big }]);
    window.setTimeout(() => setFloats((current) => current.filter((f) => f.id !== id)), 1700);
  }, []);

  const goToGrove = useCallback(
    (id: string) => {
      const next = new URLSearchParams(params);
      next.set('g', id);
      setParams(next, { replace: true });
    },
    [params, setParams],
  );

  if (state.error) return <ErrorState message={state.error} onRetry={state.reload} />;
  if (!working || !view) {
    return (
      <div className="stack">
        <div className="pxwell skeleton" style={{ height: 60 }} />
        <div className="pxwell skeleton" style={{ height: 420 }} />
      </div>
    );
  }

  const isOwner = view.isOwner;
  const grove = working.grove;
  const groves = view.groves;
  const index = groves.findIndex((row) => row.id === grove.id);

  /** Anchors the panel above a sprite, clamped to stay on screen. */
  function positionPanel(element: HTMLElement) {
    const rect = element.getBoundingClientRect();
    setPanelPos({
      left: Math.min(window.innerWidth - 280, Math.max(8, rect.left + rect.width / 2 - 134)),
      top: Math.max(8, rect.top - 210),
    });
  }

  /* --- Layout editing ---------------------------------------------------- */

  function onMove(move: DragState) {
    setDraft((current) => {
      const local = current ?? view!;
      if (move.kind === 'pokemon') {
        return {
          ...local,
          pokemon: local.pokemon.map((entry) =>
            entry.pokemon.id === move.id ? { ...entry, x: move.x, y: move.y } : entry,
          ),
        };
      }
      return {
        ...local,
        decorations: local.decorations.map((entry) =>
          entry.id === move.id ? { ...entry, x: move.x, y: move.y } : entry,
        ),
      };
    });
  }

  async function saveLayout() {
    if (!draft) {
      setEditing(false);
      return;
    }
    setBusy(true);
    try {
      await api.put('/grove/layout', {
        groveId: grove.id,
        pokemon: draft.pokemon.map((entry) => ({
          pokemonId: entry.pokemon.id,
          x: entry.x,
          y: entry.y,
          flipped: entry.flipped,
        })),
        decorations: draft.decorations.map((entry) => ({
          id: entry.id,
          x: entry.x,
          y: entry.y,
          scale: entry.scale,
          flipped: entry.flipped,
        })),
      });
      state.setData(draft);
      setDraft(null);
      setEditing(false);
      push('success', `${grove.name} saved.`, '🌱');
    } catch (caught) {
      push('error', caught instanceof ApiError ? caught.message : 'Could not save your Grove.');
    } finally {
      setBusy(false);
    }
  }

  function cancelEdit() {
    setDraft(null);
    setEditing(false);
    setTray(null);
  }

  /* --- Interactions ------------------------------------------------------ */

  async function feed(pokemon: Pokemon, x: number, y: number) {
    setBusy(true);
    try {
      const result = await api.post<GroveFeedResult>('/grove/feed', { pokemonId: pokemon.id });

      showFloat(x, y - 4, `+${formatNumber(result.expGranted)} EXP`);
      if (result.leveledTo) {
        window.setTimeout(() => showFloat(x, y - 12, `LEVEL ${result.leveledTo}!`, true), 350);
      }

      setFedIds((current) => new Set(current).add(pokemon.id));
      setWallet(result.wallet);
      celebrate(result.unlocked);
      celebrateBadges(result.badges ?? []);

      // Patch the Pokemon in place so the EXP bar in the panel moves at once.
      const apply = (v: GroveView): GroveView => ({
        ...v,
        pokemon: v.pokemon.map((entry) =>
          entry.pokemon.id === pokemon.id ? { ...entry, pokemon: result.pokemon } : entry,
        ),
      });
      state.setData((current) => (current ? apply(current) : current));
      setSelected((current) => (current ? { ...current, pokemon: result.pokemon } : current));

      // Same phrasing the profile uses, from `lib/reciprocal.ts`. Feeding is
      // one action, and a player should not be told different things about it
      // depending on whether they clicked in a Grove or on a profile.
      if (result.isOwn) {
        push('success', `${result.pokemon.displayName} enjoyed that. +${result.expGranted} EXP`, '♥');
        if (result.leveledTo) {
          push('reward', `${result.pokemon.displayName} reached Level ${result.leveledTo}!`, '★');
        }
      } else {
        const lines = feedToast({
          pokemonName: result.pokemon.displayName,
          expGranted: result.expGranted,
          coinsEarned: result.coinsEarned,
          friendBonus: result.friendBonus,
          guildBonus: result.guildBonus,
          diminished: result.diminished,
          team: result.team,
          reciprocal: result.reciprocal,
        });
        push('success', lines.primary, '♥');
        if (result.leveledTo) {
          push('reward', `${result.pokemon.displayName} reached Level ${result.leveledTo}!`, '★');
        }
        // The reciprocal EXP floats over the scene too, not only as a toast:
        // the Grove is where the sprites are, and a number rising off the
        // Pokemon you just fed is what makes the exchange legible.
        if (result.team && result.team.total > 0) {
          window.setTimeout(
            () => showFloat(x, y - 20, `TEAM +${formatNumber(result.team!.total)}`, true),
            180,
          );
        }
        if (lines.team) push('info', lines.team, '✦');
        if (lines.levels) push('reward', lines.levels, '★');
        if (lines.note) push('info', lines.note);
      }
      if (result.canEvolve) {
        push('info', `${result.pokemon.displayName} is ready to evolve!`, '⟳');
      }

      void refresh();
      activity.reload();
    } catch (caught) {
      push('error', caught instanceof ApiError ? caught.message : 'Could not feed that Pokémon.');
      if (caught instanceof ApiError && caught.code.includes('COOLDOWN')) {
        setFedIds((current) => new Set(current).add(pokemon.id));
      }
    } finally {
      setBusy(false);
    }
  }

  async function pet(pokemon: Pokemon, x: number, y: number) {
    setBusy(true);
    try {
      const result = await api.post<{ friendship: number }>('/grove/pet', { pokemonId: pokemon.id });
      showFloat(x, y - 4, '♥');
      setSelected((current) =>
        current ? { ...current, pokemon: { ...current.pokemon, friendship: result.friendship } } : current,
      );
    } catch (caught) {
      push('error', caught instanceof ApiError ? caught.message : 'Could not do that.');
    } finally {
      setBusy(false);
    }
  }

  async function removeFromGrove(pokemon: Pokemon) {
    setBusy(true);
    try {
      await api.post('/grove/remove', { pokemonId: pokemon.id });
      state.setData((current) =>
        current
          ? { ...current, pokemon: current.pokemon.filter((entry) => entry.pokemon.id !== pokemon.id) }
          : current,
      );
      setDraft(null);
      setSelected(null);
      push('info', `${pokemon.displayName} went back to your collection.`);
    } catch (caught) {
      push('error', caught instanceof ApiError ? caught.message : 'Could not remove that Pokémon.');
    } finally {
      setBusy(false);
    }
  }

  async function visitRandom() {
    try {
      const result = await api.get<{ username: string | null }>('/community/random', {
        exclude: view?.owner.username ?? '',
      });
      if (result.username) navigate(`/grove/${result.username}`);
      else push('info', 'No one to visit right now — check back later!');
    } catch {
      push('error', 'Could not find anyone right now.');
    }
  }

  const slotsUsed = working.pokemon.length;

  return (
    <div className="stack">
      {!isOwner && (
        <div className="pxframe grove-visiting">
          <span>
            You're visiting {view.owner.trainerName}'s Groves
            {view.owner.isNpc && (
              <span className="chip chip--npc chip--sm" style={{ marginLeft: 6 }}>
                🌿 NPC
              </span>
            )}
          </span>
          <span className="spacer" />
          <span style={{ fontWeight: 400 }}>Feed as many as you like</span>
          <Button size="sm" onClick={() => void visitRandom()}>
            Visit someone else →
          </Button>
        </div>
      )}

      {/* --- Grove manager ------------------------------------------------- */}
      <GroveManager
        groves={groves}
        current={grove}
        index={index}
        isOwner={isOwner}
        slots={view.groveSlots}
        onSelect={goToGrove}
        onManage={() => setTray('manage')}
      />

      <div className="page-head">
        <div>
          <h1 className="page-head__title">{grove.name}</h1>
          <div className="page-head__sub">
            {isOwner ? (
              <>
                {slotsUsed} of {grove.capacity} here · {formatNumber(working.totalOwned)} Pokémon in
                your collection
              </>
            ) : (
              <>
                <Link to={`/trainer/${view.owner.username}`}>@{view.owner.username}</Link> ·{' '}
                {view.owner.title} · {slotsUsed} Pokémon living here
              </>
            )}
          </div>
        </div>
        {isOwner && !editing && (
          <div className="row row--wrap">
            <Button variant="go" onClick={() => void visitRandom()}>
              ♥ Visit a Grove
            </Button>
            <Button variant="info" onClick={() => setEditing(true)}>
              ✎ Edit Grove
            </Button>
            <Button variant="ghost" onClick={() => photo.setActive(true)}>
              ▣ Photo Mode
            </Button>
          </div>
        )}
      </div>

      {/* Photo Mode wraps the scene rather than replacing it: the same
          GroveScene, the same idle behaviour, with the chrome hidden and a
          view transform applied. Rebuilding a second read-only scene for
          photographs is how the two drift apart. */}
      {photo.active && (
        <PhotoModeBar
          settings={photo.settings}
          onChange={photo.setSettings}
          onExit={() => photo.setActive(false)}
          busy={photo.busy}
          availableWeather={['clear', 'rain', 'snow', 'petals', 'fog']}
          onCapture={() =>
            void photo
              .capture(`pokegrove-${grove.name.toLowerCase().replace(/\s+/g, '-')}.png`)
              .catch(() => push('error', 'Could not save that photo.'))
          }
        />
      )}

      <div className="grove-hud">
        <div className="stack" style={{ gap: 'var(--px)' }}>
          <PhotoFrame settings={photo.settings} onChange={photo.setSettings} innerRef={photo.frame}>
          <GroveScene
            backgroundKey={grove.backgroundKey}
            groundStyle={grove.groundStyle}
            weatherKey={grove.weatherKey}
            pokemon={working.pokemon}
            decorations={working.decorations}
            editing={editing}
            selectedId={selected?.pokemon.id ?? null}
            fedIds={fedIds}
            canFeed
            floats={floats}
            onMove={onMove}
            onSelect={(placement, element) => {
              cancelClose();
              positionPanel(element);
              setSelected({
                pokemon: placement.pokemon,
                x: placement.x,
                y: placement.y,
                pinned: true,
              });
            }}
            onHover={(placement, element) => {
              cancelClose();
              // A hover over a different Pokemon while one is pinned should not
              // yank the panel away from what the player deliberately opened.
              setSelected((current) => {
                if (current?.pinned) return current;
                positionPanel(element);
                return {
                  pokemon: placement.pokemon,
                  x: placement.x,
                  y: placement.y,
                  pinned: false,
                };
              });
            }}
            onHoverEnd={scheduleClose}
            onSelectDecoration={() => setSelected(null)}
          />
          </PhotoFrame>

          {isOwner && (
            <div className="pxframe grove-toolbar">
              {editing ? (
                <>
                  <Button size="sm" variant="go" onClick={() => void saveLayout()} loading={busy}>
                    Save Grove
                  </Button>
                  <Button size="sm" onClick={cancelEdit}>
                    Cancel
                  </Button>
                  <span className="spacer" />
                  <Button size="sm" variant={tray === 'pokemon' ? 'info' : 'default'} onClick={() => setTray(tray === 'pokemon' ? null : 'pokemon')}>
                    + Pokémon
                  </Button>
                  <Button size="sm" variant={tray === 'decor' ? 'info' : 'default'} onClick={() => setTray(tray === 'decor' ? null : 'decor')}>
                    + Decorations
                  </Button>
                  <Button size="sm" variant={tray === 'look' ? 'info' : 'default'} onClick={() => setTray(tray === 'look' ? null : 'look')}>
                    Appearance
                  </Button>
                </>
              ) : (
                <>
                  <span className="tiny muted">
                    Tap a Pokémon to feed it. They wander on their own — drag them in Edit mode.
                  </span>
                  <span className="spacer" />
                  <span className="tiny mono">
                    {slotsUsed}/{grove.capacity}
                  </span>
                </>
              )}
            </div>
          )}
        </div>

        {/* --- Side panel --------------------------------------------------- */}
        <div className="stack">
          <Panel title="🌿 This Grove" hint={`${index + 1} of ${groves.length}`}>
            <div className="stack-sm">
              <div className="row row--between tiny">
                <span className="muted">Living here</span>
                <span className="mono">
                  {slotsUsed} / {grove.capacity}
                </span>
              </div>
              <Bar ratio={slotsUsed / grove.capacity} variant="ready" />
              <div className="row row--between tiny">
                <span className="muted">Decorations</span>
                <span className="mono">
                  {working.decorations.length} / {grove.decorCapacity}
                </span>
              </div>
              {isOwner && (
                <>
                  <hr className="divider" />
                  <div className="row row--between tiny">
                    <span className="muted">Groves used</span>
                    <span className="mono">
                      {groves.length} / {view.groveSlots}
                    </span>
                  </div>
                  <Button size="sm" block onClick={() => setTray('manage')}>
                    ⚙ Manage Groves
                  </Button>
                </>
              )}
              {!isOwner && (
                <>
                  <hr className="divider" />
                  <div className="row row--between tiny">
                    <span className="muted">Trainer level</span>
                    <span className="mono">{view.owner.level}</span>
                  </div>
                  <div className="row row--between tiny">
                    <span className="muted">Hatched</span>
                    <span className="mono">{formatNumber(view.owner.totalHatched)}</span>
                  </div>
                  <div className="row row--between tiny">
                    <span className="muted">Shinies</span>
                    <span className="mono">{formatNumber(view.owner.shinyCount)}</span>
                  </div>
                  <Link className="btn btn--sm btn--block" to={`/trainer/${view.owner.username}`}>
                    View full profile
                  </Link>
                </>
              )}
            </div>
          </Panel>

          <Panel title="◈ Activity">
            {activity.data && activity.data.activity.length > 0 ? (
              <div className="grove-activity">
                {activity.data.activity.map((entry) => (
                  <div key={entry.id} className="grove-activity__row">
                    <span className="grove-activity__icon" aria-hidden>
                      {entry.icon}
                    </span>
                    {entry.sprite && <img className="grove-activity__sprite pixel" src={entry.sprite} alt="" />}
                    <span style={{ flex: 1, minWidth: 0 }}>{entry.text}</span>
                    <span className="muted tiny" style={{ whiteSpace: 'nowrap' }}>
                      {timeAgo(entry.at)}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <EmptyState art="♡" title="Nothing yet">
                {isOwner ? 'Visit other Groves — most trainers help back.' : 'Be the first to help out.'}
              </EmptyState>
            )}
          </Panel>
        </div>
      </div>

      {/* --- Trays -------------------------------------------------------- */}
      {tray === 'pokemon' && (
        <AddPokemonTray
          groveId={grove.id}
          groveName={grove.name}
          slotsLeft={grove.capacity - slotsUsed}
          onClose={() => setTray(null)}
          onChanged={() => {
            setDraft(null);
            state.reload();
            void refresh();
          }}
        />
      )}
      {tray === 'decor' && (
        <DecorationTray
          groveId={grove.id}
          slotsLeft={grove.decorCapacity - working.decorations.length}
          placed={working.decorations}
          onClose={() => setTray(null)}
          onChanged={() => {
            setDraft(null);
            state.reload();
          }}
        />
      )}
      {tray === 'look' && (
        <AppearanceTray
          grove={grove}
          onClose={() => setTray(null)}
          onChanged={() => {
            setDraft(null);
            state.reload();
          }}
        />
      )}
      {tray === 'manage' && (
        <ManageGrovesTray
          groves={groves}
          slots={view.groveSlots}
          currentId={grove.id}
          onClose={() => setTray(null)}
          onChanged={() => state.reload()}
          onSelect={(id) => {
            goToGrove(id);
            setTray(null);
          }}
        />
      )}

      {/* --- Pokemon interaction panel ------------------------------------ */}
      {selected && !editing && (
        <PokemonPanel
          pokemon={selected.pokemon}
          position={panelPos}
          isOwner={isOwner}
          groves={groves}
          currentGroveId={grove.id}
          alreadyFed={fedIds.has(selected.pokemon.id)}
          busy={busy}
          pinned={selected.pinned}
          onFeed={() => void feed(selected.pokemon, selected.x, selected.y)}
          onPet={() => void pet(selected.pokemon, selected.x, selected.y)}
          onRemove={() => void removeFromGrove(selected.pokemon)}
          onMoveTo={async (targetId) => {
            setBusy(true);
            try {
              await api.post('/grove/move', {
                pokemonIds: [selected.pokemon.id],
                groveId: targetId,
              });
              const target = groves.find((row) => row.id === targetId);
              push('success', `${selected.pokemon.displayName} moved to ${target?.name ?? 'the Grove'}.`, '🌿');
              setSelected(null);
              state.reload();
            } catch (caught) {
              push('error', caught instanceof ApiError ? caught.message : 'Could not move that Pokémon.');
            } finally {
              setBusy(false);
            }
          }}
          onClose={() => setSelected(null)}
          onPointerEnter={cancelClose}
          onPointerLeave={scheduleClose}
        />
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------
   Grove manager
   ------------------------------------------------------------------------- */

/**
 * Previous / current / next, plus a dropdown for jumping anywhere.
 *
 * Both together on purpose: arrows are how you browse someone's Groves in
 * order, and the dropdown is how you get to the one you want when you already
 * know its name. Ten habitats is too many for arrows alone and too few for a
 * dropdown to be the only way in.
 */
function GroveManager({
  groves,
  current,
  index,
  isOwner,
  slots,
  onSelect,
  onManage,
}: {
  groves: Grove[];
  current: Grove;
  index: number;
  isOwner: boolean;
  slots: number;
  onSelect: (id: string) => void;
  onManage: () => void;
}) {
  const previous = groves[index - 1];
  const next = groves[index + 1];

  return (
    <div className="pxframe grove-manager">
      <Button
        size="sm"
        disabled={!previous}
        onClick={() => previous && onSelect(previous.id)}
        title={previous?.name}
      >
        ← {previous ? previous.name : 'Previous'}
      </Button>

      <label className="grove-manager__select">
        <span className="sr-only">Choose a Grove</span>
        <select
          className="input"
          value={current.id}
          onChange={(event) => onSelect(event.target.value)}
        >
          {groves.map((grove) => (
            <option key={grove.id} value={grove.id}>
              {grove.name} ({grove.pokemonCount}/{grove.capacity})
            </option>
          ))}
        </select>
      </label>

      <Button size="sm" disabled={!next} onClick={() => next && onSelect(next.id)} title={next?.name}>
        {next ? next.name : 'Next'} →
      </Button>

      {isOwner && (
        <>
          <span className="spacer" />
          <span className="tiny mono muted">
            {groves.length}/{slots} Groves
          </span>
          <Button size="sm" onClick={onManage}>
            ⚙
          </Button>
        </>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------
   Interaction panel
   ------------------------------------------------------------------------- */

function PokemonPanel({
  pokemon,
  position,
  isOwner,
  groves,
  currentGroveId,
  alreadyFed,
  busy,
  pinned,
  onFeed,
  onPet,
  onRemove,
  onMoveTo,
  onClose,
  onPointerEnter,
  onPointerLeave,
}: {
  pokemon: Pokemon;
  position: { left: number; top: number };
  isOwner: boolean;
  groves: Grove[];
  currentGroveId: string;
  alreadyFed: boolean;
  busy: boolean;
  /** True when opened by a click. Hover-opened panels close by moving away. */
  pinned: boolean;
  onFeed: () => void;
  onPet: () => void;
  onRemove: () => void;
  onMoveTo: (groveId: string) => void;
  onClose: () => void;
  onPointerEnter: () => void;
  onPointerLeave: () => void;
}) {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => event.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const elsewhere = groves.filter((grove) => grove.id !== currentGroveId);

  return (
    <>
      {/* Click-away layer; transparent so the scene stays visible. Its depth
          comes from the token scale rather than a literal, because it has to
          stay exactly one below `.mon-panel` — a hardcoded number here once
          drifted above the panel and ate every click on Feed.

          Only rendered for a pinned (clicked) panel. It covers the viewport, so
          leaving it up during hover would block the pointer from ever reaching
          the next Pokemon and the panel could never follow the mouse. */}
      {pinned && (
        <div
          style={{ position: 'fixed', inset: 0, zIndex: 'var(--z-popover-scrim)' }}
          onClick={onClose}
        />
      )}
      <div
        className="pxframe pxframe--raised mon-panel"
        style={{ left: position.left, top: position.top }}
        onPointerEnter={onPointerEnter}
        onPointerLeave={onPointerLeave}
      >
        <div className="stack-sm">
          <div className="mon-panel__head">
            <img className="mon-panel__sprite pixel" src={pokemon.sprite} alt="" />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="mono tiny">{pokemon.displayName}</div>
              <div className="tiny muted">Level {pokemon.level}</div>
            </div>
            <button className="btn btn--ghost btn--sm" onClick={onClose} aria-label="Close">
              ✕
            </button>
          </div>

          <Bar
            ratio={pokemon.expRatio}
            variant="exp"
            label={pokemon.isMaxLevel ? 'MAX' : `${Math.round(pokemon.expRatio * 100)}%`}
          />

          <div className="row row--between tiny muted">
            <span>♥ Friendship {pokemon.friendship}</span>
            <span>Fed {formatNumber(pokemon.interactionCount)}×</span>
          </div>

          <div className="row" style={{ gap: 4 }}>
            <Button
              size="sm"
              variant={alreadyFed ? 'default' : 'go'}
              disabled={alreadyFed || busy}
              loading={busy}
              onClick={onFeed}
              style={{ flex: 1 }}
            >
              {alreadyFed ? '✓ Fed' : 'Feed'}
            </Button>
            {isOwner && (
              <Button size="sm" disabled={busy} onClick={onPet}>
                Pet
              </Button>
            )}
            <Link className="btn btn--sm btn--info" to={`/pokemon/${pokemon.id}`}>
              View
            </Link>
          </div>

          {isOwner && elsewhere.length > 0 && (
            <label className="field">
              <span className="field__label">Move to another Grove</span>
              <select
                className="input"
                value=""
                disabled={busy}
                onChange={(event) => event.target.value && onMoveTo(event.target.value)}
              >
                <option value="">Choose a Grove…</option>
                {elsewhere.map((grove) => (
                  <option key={grove.id} value={grove.id} disabled={grove.pokemonCount >= grove.capacity}>
                    {grove.name} ({grove.pokemonCount}/{grove.capacity})
                  </option>
                ))}
              </select>
            </label>
          )}

          {isOwner && (
            <Button size="sm" variant="ghost" disabled={busy} onClick={onRemove}>
              Return to collection
            </Button>
          )}

          {isOwner && (
            <p className="tiny muted" style={{ margin: 0 }}>
              Your own food is a light snack. Visitors give far more EXP.
            </p>
          )}
        </div>
      </div>
    </>
  );
}

/* -------------------------------------------------------------------------
   Trays
   ------------------------------------------------------------------------- */

function ManageGrovesTray({
  groves,
  slots,
  currentId,
  onClose,
  onChanged,
  onSelect,
}: {
  groves: Grove[];
  slots: number;
  currentId: string;
  onClose: () => void;
  onChanged: () => void;
  onSelect: (id: string) => void;
}) {
  const { push } = useToast();
  const [busy, setBusy] = useState(false);
  const [order, setOrder] = useState(groves);
  const [renaming, setRenaming] = useState<string | null>(null);
  const [name, setName] = useState('');

  useEffect(() => setOrder(groves), [groves]);

  async function createGrove() {
    setBusy(true);
    try {
      await api.post('/grove/new', {});
      push('success', 'New Grove ready.', '🌿');
      onChanged();
    } catch (caught) {
      push('error', caught instanceof ApiError ? caught.message : 'Could not add a Grove.');
    } finally {
      setBusy(false);
    }
  }

  async function rename(groveId: string) {
    setBusy(true);
    try {
      await api.patch('/grove/appearance', { groveId, name });
      setRenaming(null);
      onChanged();
    } catch (caught) {
      push('error', caught instanceof ApiError ? caught.message : 'Could not rename that Grove.');
    } finally {
      setBusy(false);
    }
  }

  async function remove(grove: Grove) {
    setBusy(true);
    try {
      await api.del(`/grove/${grove.id}`);
      push('info', `${grove.name} removed.`);
      onChanged();
    } catch (caught) {
      push('error', caught instanceof ApiError ? caught.message : 'Could not remove that Grove.');
    } finally {
      setBusy(false);
    }
  }

  /** Moves one Grove up or down, then persists the whole order. */
  async function shift(groveId: string, direction: -1 | 1) {
    const from = order.findIndex((grove) => grove.id === groveId);
    const to = from + direction;
    if (from < 0 || to < 0 || to >= order.length) return;

    const next = [...order];
    [next[from], next[to]] = [next[to]!, next[from]!];
    setOrder(next);

    try {
      await api.put('/grove/order', { groveIds: next.map((grove) => grove.id) });
      onChanged();
    } catch {
      setOrder(groves);
      push('error', 'Could not save that order.');
    }
  }

  return (
    <Modal onClose={onClose} labelledBy="manage-groves-title" wide>
      <div className="stack">
        <div className="row row--between">
          <h2 id="manage-groves-title">Your Groves</h2>
          <span className="chip">
            {order.length} / {slots} used
          </span>
        </div>

        <p className="tiny muted" style={{ margin: 0 }}>
          Groves are habitats to sort your collection into. Every one holds the same number of
          Pokémon — you earn more Groves, never bigger ones.
        </p>

        <div className="stack-sm">
          {order.map((grove, index) => (
            <div key={grove.id} className="pxwell grove-manage__row">
              <div className="grove-manage__order">
                <button
                  type="button"
                  className="btn btn--ghost btn--sm"
                  disabled={index === 0 || busy}
                  onClick={() => void shift(grove.id, -1)}
                  aria-label={`Move ${grove.name} up`}
                >
                  ▲
                </button>
                <button
                  type="button"
                  className="btn btn--ghost btn--sm"
                  disabled={index === order.length - 1 || busy}
                  onClick={() => void shift(grove.id, 1)}
                  aria-label={`Move ${grove.name} down`}
                >
                  ▼
                </button>
              </div>

              <span
                className={`grove-manage__swatch grove-bg--${grove.backgroundKey}`}
                aria-hidden
              />

              <div style={{ flex: 1, minWidth: 0 }}>
                {renaming === grove.id ? (
                  <div className="row" style={{ gap: 4 }}>
                    <input
                      className="input"
                      value={name}
                      maxLength={32}
                      autoFocus
                      onChange={(event) => setName(event.target.value)}
                    />
                    <Button size="sm" variant="go" disabled={busy} onClick={() => void rename(grove.id)}>
                      Save
                    </Button>
                    <Button size="sm" onClick={() => setRenaming(null)}>
                      Cancel
                    </Button>
                  </div>
                ) : (
                  <>
                    <div className="mono tiny">{grove.name}</div>
                    <div className="tiny muted">
                      {grove.pokemonCount}/{grove.capacity} Pokémon ·{' '}
                      {grove.decorationCount} decorations
                    </div>
                  </>
                )}
              </div>

              {renaming !== grove.id && (
                <div className="row" style={{ gap: 4 }}>
                  {grove.id !== currentId && (
                    <Button size="sm" variant="info" onClick={() => onSelect(grove.id)}>
                      Open
                    </Button>
                  )}
                  <Button
                    size="sm"
                    onClick={() => {
                      setRenaming(grove.id);
                      setName(grove.name);
                    }}
                  >
                    Rename
                  </Button>
                  {/* Refused server-side while anything lives here — "delete"
                      quietly evicting thirty Pokemon is the kind of destructive
                      convenience you only notice afterwards. */}
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={busy || grove.pokemonCount > 0 || order.length <= 1}
                    title={
                      grove.pokemonCount > 0
                        ? 'Move its Pokémon somewhere else first'
                        : order.length <= 1
                          ? 'You need at least one Grove'
                          : undefined
                    }
                    onClick={() => void remove(grove)}
                  >
                    Delete
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>

        <Button
          block
          variant="go"
          disabled={busy || order.length >= slots}
          onClick={() => void createGrove()}
        >
          {order.length >= slots ? 'All Grove slots in use' : '+ New Grove'}
        </Button>

        <Button block onClick={onClose}>
          Done
        </Button>
      </div>
    </Modal>
  );
}

function AddPokemonTray({
  groveId,
  groveName,
  slotsLeft,
  onClose,
  onChanged,
}: {
  groveId: string;
  groveName: string;
  slotsLeft: number;
  onClose: () => void;
  onChanged: () => void;
}) {
  const { push } = useToast();
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [chosen, setChosen] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(search), 250);
    return () => window.clearTimeout(timer);
  }, [search]);

  const state = useAsync<{ pokemon: Pokemon[] }>(
    () => api.get('/grove/available/list', { search: debounced || undefined }),
    [debounced],
  );

  /**
   * Multi-select, then one move. Sorting a collection means putting *groups* of
   * Pokemon somewhere — clicking twelve Water types in one at a time, each with
   * its own round trip, is the tedium this whole redesign exists to remove.
   */
  async function addChosen() {
    if (chosen.size === 0) return;
    setBusy(true);
    try {
      await api.post('/grove/move', { pokemonIds: [...chosen], groveId });
      push('success', `${chosen.size} moved into ${groveName}.`, '🌱');
      setChosen(new Set());
      onChanged();
      state.reload();
    } catch (caught) {
      push('error', caught instanceof ApiError ? caught.message : 'Could not add those Pokémon.');
    } finally {
      setBusy(false);
    }
  }

  const toggle = (id: string) =>
    setChosen((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else if (next.size < slotsLeft) next.add(id);
      return next;
    });

  return (
    <Modal onClose={onClose} labelledBy="add-mon-title" wide>
      <div className="stack">
        <div className="row row--between">
          <h2 id="add-mon-title">Add to {groveName}</h2>
          <span className="chip">
            {slotsLeft} space{slotsLeft === 1 ? '' : 's'} free
          </span>
        </div>

        <input
          className="input"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search your collection"
        />

        {slotsLeft <= 0 && (
          <div className="pxwell tiny" style={{ padding: 'var(--gap-sm)' }}>
            This Grove is full. Move a Pokémon out, or put these in another Grove.
          </div>
        )}

        {state.loading ? (
          <div className="pxwell skeleton" style={{ height: 200 }} />
        ) : state.data && state.data.pokemon.length > 0 ? (
          <div className="grove-tray">
            {state.data.pokemon.map((pokemon) => (
              <button
                key={pokemon.id}
                type="button"
                className={`pxframe tray-item ${pokemon.isShiny ? 'pkmn-card--shiny' : ''} ${
                  chosen.has(pokemon.id) ? 'tray-item--chosen' : ''
                }`}
                disabled={slotsLeft <= 0 || (!chosen.has(pokemon.id) && chosen.size >= slotsLeft)}
                onClick={() => toggle(pokemon.id)}
                aria-pressed={chosen.has(pokemon.id)}
              >
                <img className="pixel" src={pokemon.sprite} alt="" loading="lazy" />
                <span className="tray-item__name">{pokemon.displayName}</span>
                <span className="tiny muted">Lv {pokemon.level}</span>
              </button>
            ))}
          </div>
        ) : (
          <EmptyState art="◓" title="Everything you own is already in a Grove">
            Hatch more eggs, or move some back to your collection.
          </EmptyState>
        )}

        <div className="row">
          <Button
            variant="go"
            disabled={chosen.size === 0 || busy}
            loading={busy}
            onClick={() => void addChosen()}
            style={{ flex: 1 }}
          >
            {chosen.size === 0 ? 'Choose some Pokémon' : `Move ${chosen.size} in`}
          </Button>
          <Button onClick={onClose}>Done</Button>
        </div>
      </div>
    </Modal>
  );
}

function DecorationTray({
  groveId,
  slotsLeft,
  placed,
  onClose,
  onChanged,
}: {
  groveId: string;
  slotsLeft: number;
  placed: GroveDecoration[];
  onClose: () => void;
  onChanged: () => void;
}) {
  const { push } = useToast();
  const [tab, setTab] = useState<'place' | 'remove'>('place');
  const [busy, setBusy] = useState<string | null>(null);
  const state = useAsync<{ decorations: GroveDecorStock[] }>(() =>
    api.get('/grove/decorations', { groveId }),
  );

  async function place(stock: GroveDecorStock) {
    setBusy(stock.itemKey);
    try {
      await api.post('/grove/decorations/place', { groveId, itemKey: stock.itemKey, x: 50, y: 72 });
      push('success', `${stock.name} placed. Drag it where you like!`, '🌿');
      onChanged();
      state.reload();
    } catch (caught) {
      push('error', caught instanceof ApiError ? caught.message : 'Could not place that.');
    } finally {
      setBusy(null);
    }
  }

  async function remove(decoration: GroveDecoration) {
    setBusy(decoration.id);
    try {
      await api.post('/grove/decorations/remove', { id: decoration.id });
      push('info', `${decoration.name} put away.`);
      onChanged();
      state.reload();
    } catch (caught) {
      push('error', caught instanceof ApiError ? caught.message : 'Could not remove that.');
    } finally {
      setBusy(null);
    }
  }

  const stock = state.data?.decorations ?? [];

  return (
    <Modal onClose={onClose} labelledBy="decor-title" wide>
      <div className="stack">
        <div className="row row--between">
          <h2 id="decor-title">Decorations</h2>
          <span className="chip">
            {slotsLeft} space{slotsLeft === 1 ? '' : 's'} free
          </span>
        </div>

        <Tabs
          value={tab}
          onChange={setTab}
          tabs={[
            { value: 'place', label: 'Place' },
            { value: 'remove', label: 'Placed', badge: placed.length },
          ]}
        />

        {tab === 'place' ? (
          stock.length === 0 ? (
            <EmptyState
              art="🌿"
              title="No decorations yet"
              action={
                <Link className="btn btn--primary" to="/shop">
                  Visit the Shop
                </Link>
              }
            >
              Decorations are bought in the Shop and unlock as you level up.
            </EmptyState>
          ) : (
            <div className="grove-tray">
              {stock.map((item) => (
                <button
                  key={item.itemKey}
                  type="button"
                  className="pxframe tray-item"
                  disabled={item.available <= 0 || slotsLeft <= 0 || busy === item.itemKey}
                  onClick={() => void place(item)}
                  title={
                    item.available <= 0
                      ? 'All of yours are placed in other Groves'
                      : item.description
                  }
                >
                  <span className="tray-item__art">
                    <span className={`decor__art art-${item.art}`} style={{ transform: 'scale(.55)' }} aria-hidden />
                  </span>
                  <span className="tray-item__name">{item.name}</span>
                  <span className="tiny muted">
                    {item.available} / {item.owned} free
                  </span>
                </button>
              ))}
            </div>
          )
        ) : placed.length === 0 ? (
          <EmptyState art="🌿" title="Nothing placed yet" />
        ) : (
          <div className="grove-tray">
            {placed.map((decoration) => (
              <button
                key={decoration.id}
                type="button"
                className="pxframe tray-item"
                disabled={busy === decoration.id}
                onClick={() => void remove(decoration)}
              >
                <span className="tray-item__art">
                  <span className={`decor__art art-${decoration.art}`} style={{ transform: 'scale(.55)' }} aria-hidden />
                </span>
                <span className="tray-item__name">{decoration.name}</span>
                <span className="tiny" style={{ color: 'var(--berry-dark)' }}>
                  Remove
                </span>
              </button>
            ))}
          </div>
        )}

        <Button block onClick={onClose}>
          Done
        </Button>
      </div>
    </Modal>
  );
}

function AppearanceTray({
  grove,
  onClose,
  onChanged,
}: {
  grove: Grove;
  onClose: () => void;
  onChanged: () => void;
}) {
  const { push } = useToast();
  const [name, setName] = useState(grove.name);
  const [busy, setBusy] = useState(false);
  const state = useAsync<{
    backgrounds: GroveBackgroundOption[];
    groundStyles: string[];
    weatherKeys: string[];
  }>(() => api.get('/grove/backgrounds'));

  async function apply(patch: Record<string, unknown>) {
    setBusy(true);
    try {
      await api.patch('/grove/appearance', { groveId: grove.id, ...patch });
      onChanged();
    } catch (caught) {
      push('error', caught instanceof ApiError ? caught.message : 'Could not change that.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal onClose={onClose} labelledBy="look-title" wide>
      <div className="stack">
        <h2 id="look-title">Grove appearance</h2>

        <div className="field">
          <label className="field__label" htmlFor="grove-name">
            Grove name
          </label>
          <div className="row">
            <input
              id="grove-name"
              className="input"
              value={name}
              maxLength={32}
              onChange={(event) => setName(event.target.value)}
            />
            <Button size="sm" disabled={busy || name === grove.name} onClick={() => void apply({ name })}>
              Save
            </Button>
          </div>
        </div>

        <div className="stack-sm">
          <div className="field__label">Ground</div>
          <div className="row row--wrap">
            {(state.data?.groundStyles ?? []).map((style) => (
              <Button
                key={style}
                size="sm"
                variant={grove.groundStyle === style ? 'go' : 'default'}
                disabled={busy}
                onClick={() => void apply({ groundStyle: style })}
              >
                {style}
              </Button>
            ))}
          </div>
        </div>

        <div className="stack-sm">
          <div className="field__label">Weather</div>
          <div className="row row--wrap">
            {(state.data?.weatherKeys ?? []).map((key) => (
              <Button
                key={key}
                size="sm"
                variant={grove.weatherKey === key ? 'go' : 'default'}
                disabled={busy}
                onClick={() => void apply({ weatherKey: key })}
              >
                {key}
              </Button>
            ))}
          </div>
        </div>

        <div className="stack-sm">
          <div className="field__label">Backdrop</div>
          <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))' }}>
            {(state.data?.backgrounds ?? []).map((background) => (
              <button
                key={background.key}
                type="button"
                className="pxframe"
                disabled={!background.unlocked || busy}
                onClick={() => void apply({ backgroundKey: background.key })}
                style={{
                  padding: 0,
                  border: 0,
                  cursor: background.unlocked ? 'pointer' : 'not-allowed',
                  outline: grove.backgroundKey === background.key ? '3px solid var(--leaf)' : 'none',
                  outlineOffset: 3,
                  opacity: background.unlocked ? 1 : 0.55,
                  overflow: 'hidden',
                }}
              >
                <span
                  className={`grove grove-bg--${background.art} grove-ground--grass`}
                  style={{ display: 'block', height: 74, minHeight: 0, aspectRatio: 'auto', boxShadow: 'none' }}
                >
                  <span className="grove__sky" />
                  <span className="grove__horizon" />
                  <span className="grove__ground grove-ground--grass" />
                </span>
                <span
                  className="tray-item__name"
                  style={{ display: 'block', padding: '5px 4px', background: 'var(--paper)' }}
                >
                  {background.name}
                  {!background.unlocked && (
                    <span className="tiny muted" style={{ display: 'block' }}>
                      Trainer Lv {background.unlockTrainerLevel}
                    </span>
                  )}
                </span>
              </button>
            ))}
          </div>
        </div>

        <Button block onClick={onClose}>
          Done
        </Button>
      </div>
    </Modal>
  );
}
